// Lecture des permissions effectives du contexte actif (rôles, délégations, habilitations),
// telles que calculées par le serveur et renvoyées par GET /contexte.
import type { ContexteActif } from '../api/types';

export function peut(contexte: ContexteActif | null | undefined, domaine: string, permission: string): boolean {
  const actif = contexte?.appartenance_active;
  if (!actif) return false;
  if (actif.permissions[domaine]?.includes(permission)) return true;
  return actif.delegations.some((d) => d.domaine === domaine && d.permissions.includes(permission));
}

/** Au moins une permission sur le domaine. */
export function voitDomaine(contexte: ContexteActif | null | undefined, domaine: string): boolean {
  const actif = contexte?.appartenance_active;
  if (!actif) return false;
  return (actif.permissions[domaine]?.length ?? 0) > 0 || actif.delegations.some((d) => d.domaine === domaine);
}

/**
 * Le module est-il activé dans la collectivité active (H9-01) ? null tant que la liste n'est pas connue
 * (contexte absent, serveur qui ne l'annonce pas encore) : les écrans ne masquent rien dans ce cas.
 */
export function moduleActif(contexte: ContexteActif | null | undefined, module: string): boolean | null {
  const liste = contexte?.appartenance_active?.modules_actifs;
  if (!liste) return null;
  return liste.includes(module);
}

/** Codes des rôles actifs dans l'appartenance active. */
export function rolesActifs(contexte: ContexteActif | null | undefined): string[] {
  const actif = contexte?.appartenance_active;
  if (!actif) return [];
  const a = contexte.appartenances.find((x) => x.id === actif.id);
  return (a?.roles ?? []).filter((r) => r.statut === 'active').map((r) => r.code);
}

export function nomCompte(contexte: ContexteActif | null | undefined): string {
  if (!contexte) return '';
  return [contexte.compte.prenoms, contexte.compte.nom].filter(Boolean).join(' ');
}

export const LIBELLES_DOMAINES: Record<string, string> = {
  courrier: 'Courriers',
  dossier: 'Dossiers',
  demarche_config: 'Configuration des démarches',
  document: 'Documents',
  budget: 'Budget',
  depense: 'Dépenses',
  recette: 'Recettes',
  caisse: 'Caisse',
  marche_municipal: 'Marchés municipaux',
  domaine_public: 'Domaine public',
  patrimoine: 'Patrimoine',
  personnel: 'Personnel',
  personnel_sensible: 'Personnel (sensible)',
  intervention: 'Interventions',
  dechets: 'Déchets et propreté',
  travaux: 'Travaux',
  achat: 'Achats',
  assemblee: 'Assemblée',
  quartier: 'Quartiers',
  urbanisme: 'Urbanisme',
  equipement: 'Équipements',
  social: 'Action sociale',
  evenement: 'Événements',
  reclamation: 'Réclamations',
  reclamation_sensible: 'Réclamations (sensibles)',
  inspection: 'Inspections',
  messagerie: 'Messagerie',
  parametres: 'Paramètres',
  utilisateurs: 'Utilisateurs',
  audit: 'Journal d\'audit',
  supervision: 'Supervision',
};

export const LIBELLES_PERMISSIONS: Record<string, string> = {
  voir: 'voir',
  creer: 'créer',
  modifier: 'modifier',
  transmettre: 'transmettre',
  valider: 'valider',
  decider: 'décider',
  signer: 'signer',
  refuser: 'refuser',
  archiver: 'archiver',
  exporter: 'exporter',
  administrer: 'administrer',
};
